import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateAppOpenedStreakConfig } from "../../services/rewards.service";
import { parseInteger, getFieldErrorMessage } from "../../utils/rewardsConfig";

const AppOpenedStreakBlock = ({ config, onSuccess }) => {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(config?.enabled ?? false);
  const [streakDays, setStreakDays] = useState(
    config?.streakDays !== null ? String(config.streakDays) : "",
  );
  const [amountTokens, setAmountTokens] = useState(
    config?.amountTokens !== null ? String(config.amountTokens) : "",
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [genericError, setGenericError] = useState("");

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled ?? false);
      setStreakDays(
        config.streakDays !== null ? String(config.streakDays) : "",
      );
      setAmountTokens(
        config.amountTokens !== null ? String(config.amountTokens) : "",
      );
    }
  }, [config]);

  const { mutate: save, isPending } = useMutation({
    mutationFn: (body) => updateAppOpenedStreakConfig(body),
    onSuccess: (data) => {
      setFieldErrors({});
      setGenericError("");
      queryClient.setQueryData(["rewards-config"], (prev) =>
        prev ? { ...prev, appOpenedStreak: data } : { appOpenedStreak: data },
      );
      queryClient.invalidateQueries({ queryKey: ["rewards"] });
      onSuccess?.();
      alert("App opened streak config saved");
    },
    onError: (err) => {
      setFieldErrors(err.errors ?? {});
      setGenericError(err.message || "Failed to save");
    },
  });

  const handleSave = () => {
    setGenericError("");
    const streakDaysNum = parseInteger(streakDays, 0);
    const amountTokensNum = parseInteger(amountTokens, 0);
    const errors = {};
    if (streakDaysNum < 1) {
      errors.streakDays = "Must be at least 1";
    }
    if (amountTokensNum < 1) {
      errors.amountTokens = "Must be at least 1";
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    save({
      enabled,
      streakDays: streakDaysNum,
      amountTokens: amountTokensNum,
    });
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        App opened streak
      </Typography>
      {genericError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setGenericError("");
                handleSave();
              }}
            >
              Retry
            </Button>
          }
        >
          {genericError}
        </Alert>
      )}
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400 }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          }
          label="Enabled"
        />
        <TextField
          label="Streak days"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          value={streakDays}
          onChange={(e) => setStreakDays(e.target.value)}
          error={!!fieldErrors.streakDays}
          helperText={getFieldErrorMessage(fieldErrors, "streakDays")}
        />
        <TextField
          label="Amount (tokens)"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          value={amountTokens}
          onChange={(e) => setAmountTokens(e.target.value)}
          error={!!fieldErrors.amountTokens}
          helperText={getFieldErrorMessage(fieldErrors, "amountTokens")}
        />
        <Button variant="contained" onClick={handleSave} disabled={isPending}>
          {isPending ? "Saving…" : "Save"}
        </Button>
      </Box>
    </Paper>
  );
};

export default AppOpenedStreakBlock;
